import EzController from 'controllers/ez-base-controller';
import {
  scrolledIntoViewWithOffset,
  getCurrentPageParam,
  isScrolledIntoView,
} from 'lib/helpers';

import Cookies from 'js-cookie';
import { ajaxGet } from 'lib/ajax-helpers';
import { sendTrackingEvent } from 'components/analytics/conversions/tracking';
import { SEARCH_INFINITE_PAGE_TRIGGERED } from 'components/analytics/conversions/constants/actions';

const TRIGGER_OFFSET = 4000;

export default class SearchInfiniteController extends EzController {
  static targets = [
    'showMore',
    'resultsBottom',
    'spinnerBottom',
  ];

  connect() {
    this.startPage = this.getStartPage();
    this.currentPage = this.startPage;
    this.seenPages = [];
    this.loadingResults = false;
    this.stateChanged = false;
    this.startPageLastPage = this.getStartPageLast();

    this.setupInfinitePageListeners();
    this.setupStartPosition();

    this.sendPageChangeEvent(this.currentPage);

    window.addEventListener('scroll', this.trackInfinitePage);

    this.setInfiniteCookies();
  }

  disconnect() {
    window.removeEventListener('scroll', this.triggerResultsTop);
    window.removeEventListener('scroll', this.triggerResultsBottom);
    window.removeEventListener('scroll', this.trackInfinitePage);
  }

  setInfiniteCookies() {
    Cookies.set('infinite_scroll_previous_content_type', Cookies.get('content_type'));
  }

  setupInfinitePageListeners() {
    if (this.currentPage > 1) {
      this.pageNumBottom = this.currentPage;
      this.pageNumTop = this.pageNumBottom;
      this.previousPageLoaded = false;
      this.resultsTopTarget = document.querySelector('[data-search-infinite-target="resultsTop"]');
      this.spinnerTopTarget = document.querySelector('.infinite-search-spinner-top');

      this.triggerResultsTop();

      if (this.hasShowMoreTarget) {
        this.showMoreTarget.classList.add('is-hidden');
        window.addEventListener('scroll', this.triggerResultsBottom);
      }

      window.addEventListener('scroll', this.triggerResultsTop);
    } else {
      this.pageNumBottom = 1;
      this.pageNumTop = 1;
    }
  }

  setupStartPosition() {
    // wait a tick to make sure we have everything in place
    setTimeout(() => {
      const previousContentType = Cookies.get('infinite_scroll_previous_content_type');
      const gridOffset = previousContentType === Cookies.get('content_type') ? Cookies.get('infinite_scroll_offset') : 0;
      const gridEl = document.querySelector(`ul[data-infinite-grid-page="${this.startPage}"]`);

      if (gridOffset !== 0 && gridEl) {
        const scrollOffset = gridEl.offsetTop - gridOffset;
        window.scrollTo(0, scrollOffset);
      } else {
        window.scrollTo(0, 0);
      }

      if (this.previousPageLoaded !== false) {
        Cookies.remove('infinite_scroll_offset');
      }
    });
  }

  showMore() {
    this.loadingResults = true;
    this.updatePage(this.currentPage + 1);

    this.getInfiniteResults();

    this.showMoreTarget.classList.add('is-hidden');
    this.spinnerBottomTarget.classList.remove('is-hidden');

    window.addEventListener('scroll', this.triggerResultsBottom);
  }

  getInfiniteResults(top = false) {
    let page = null;

    if (top) {
      this.pageNumTop -= 1;
      page = this.pageNumTop;
      if (page < 2) {
        window.removeEventListener('scroll', this.triggerResultsTop);
      }
    } else {
      this.pageNumBottom += 1;
      page = this.pageNumBottom;
    }

    this.setUpUrlParams();

    ajaxGet('/more_infinite_search_results',
      (response) => {
        this.insertAjaxResources(response, page, top);
      },
      (error) => {
        console.error(error); // eslint-disable-line no-console
      },
      { params: { page, term: this.searchTerm, ...this.searchQueryParams } });
  }

  setUpUrlParams = () => {
    this.searchQueryParams = Array.from(new URLSearchParams(window.location.search)).reduce((o, i) => ({ ...o, [i[0]]: i[1] }), {}); // eslint-disable-line max-len

    delete this.searchQueryParams.page;
  }

  triggerResultsTop = () => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    if (scrollTop < TRIGGER_OFFSET) {
      if (this.loadingResults === false && this.pageNumTop > 1) {
        this.loadingResults = true;
        this.spinnerTopTarget.classList.remove('is-hidden');
        this.getInfiniteResults(true);
      }
    }
  }

  triggerResultsBottom = () => {
    if (scrolledIntoViewWithOffset('.ez-site-footer', TRIGGER_OFFSET)) {
      if (this.loadingResults === false) {
        this.loadingResults = true;
        this.spinnerBottomTarget.classList.remove('is-hidden');
        this.getInfiniteResults();
      }
    }
  }

  trackInfinitePage = () => {
    const grids = document.querySelectorAll('ul[data-infinite-grid-page]');

    let page = this.currentPage;

    for (let i = grids.length - 1; i >= 0; i -= 1) {
      if (isScrolledIntoView(grids[i], true)) {
        page = parseInt(grids[i].dataset.infiniteGridPage, 10);
        break;
      }
    }

    this.setPageOffset(page);

    if (this.currentPage !== page) {
      this.updatePage(page);
    }
  }

  updatePage(page) {
    this.currentPage = page;
    const url = new URL(window.location);
    url.searchParams.set('page', page);

    if (this.stateChanged) {
      window.history.replaceState(window.history.state, '', url);
    } else {
      this.stateChanged = true;
      window.history.pushState(window.history.state, '', url);
    }

    this.updateReferringPathAttr();

    this.sendPageChangeEvent(page);
  }

  updateReferringPathAttr() {
    const path = window.location.pathname + window.location.search;

    if (document.body.hasAttribute('data-referring-path')) {
      document.body.setAttribute('data-referring-path', path);
    }
  }

  sendPageChangeEvent(page) {
    if (!this.seenPages.includes(page)) {
      this.seenPages.push(page);

      sendTrackingEvent({
        element: this.element,
        data: {
          action: SEARCH_INFINITE_PAGE_TRIGGERED,
          label: `Terms-${this.searchTerm}`,
          value: page,
          content_type: `Content-${Cookies.get('content_type')}`,
        },
      });
    }
  }

  setPageOffset = (page) => {
    if (this.previousPageLoaded === false) {
      return;
    }

    if (this.pageOffsetTimeout) clearTimeout(this.pageOffsetTimeout);

    this.pageOffsetTimeout = setTimeout(() => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const offset = document.querySelector(`[data-infinite-grid-page="${page}"]`).offsetTop - scrollTop;
      Cookies.set('infinite_scroll_offset', offset);
    }, 500);
  }

  insertAjaxResources = (res, page, top) => {
    this.insertPageTop = top;
    const resultsBody = document.createElement('DIV');
    resultsBody.innerHTML = res.data;

    const infiniteGrid = resultsBody.querySelector('ul.ez-resource-grid:not(.ez-resource-grid--partner)');
    infiniteGrid.setAttribute('data-infinite-grid-page', page);

    const sponsoredResults = resultsBody.querySelector('.ez-sponsored-results');

    if (top) {
      this.saveCurrentPagePosition();
      this.resultsTopTarget.prepend(infiniteGrid);
      this.updateGridItems(this.resultsTopTarget.querySelector(`[data-infinite-grid-page="${page}"]`));

      if (sponsoredResults) {
        this.resultsTopTarget.prepend(sponsoredResults);
      }
      this.spinnerTopTarget.classList.add('is-hidden');
    } else {
      this.resultsBottomTarget.append(infiniteGrid);
      this.updateGridItems(this.resultsBottomTarget.querySelector(`[data-infinite-grid-page="${page}"]`));

      if (sponsoredResults) {
        this.resultsBottomTarget.append(sponsoredResults);
      }
      this.spinnerBottomTarget.classList.add('is-hidden');
    }

    if (top) {
      this.repositionCurrentPage();

      if (this.previousPageLoaded === false) {
        this.previousPageLoaded = true;
        this.showSidebar();

        if (!this.startPageLastPage) {
          this.loadingResults = false;
          this.triggerResultsBottom();
        }
      }
    } else {
      const isEndOfResults = resultsBody.querySelector('.ez-search-results').dataset.endResults;
      this.endOfResults(isEndOfResults);
    }

    this.loadingResults = false;
  }

  saveCurrentPagePosition() {
    const gridEl = document.querySelector(`[data-infinite-grid-page="${this.currentPage}"]`);

    if (gridEl) {
      this.currentPositionElementId = gridEl;

      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollDist = gridEl.offsetTop - scrollTop;
      this.currentPositionElementOffset = scrollDist;
    }
  }

  repositionCurrentPage() {
    if (this.currentPositionElementId) {
      const scrollOffset = this.currentPositionElementOffset || 0;
      const scrollDist = this.currentPositionElementId.offsetTop - scrollOffset;
      window.scrollTo(0, scrollDist);
    }
  }

  endOfResults(value) {
    if (value === 'true') {
      window.removeEventListener('scroll', this.triggerResultsBottom);
    }
  }

  showSidebar() {
    document.querySelector('.search-sidebar').classList.remove('invisible');
  }

  updateGridItems(grid) {
    window.ezResourceGrid({
      gridSelector: grid,
      options: {
        rowHeight: parseInt(grid.getAttribute('data-row-height'), 10),
        maxRows: parseInt(grid.getAttribute('data-max-rows'), 10),
        truncate: !!grid.getAttribute('data-truncate-results'),
      },
    });
  }

  getStartPage() {
    const savedPageNum = Cookies.get('temp-search-page-num');

    if (savedPageNum) {
      Cookies.remove('temp-search-page-num');

      return parseInt(savedPageNum, 10);
    }

    return parseInt(getCurrentPageParam(), 10) || 1;
  }

  getStartPageLast() {
    return document.querySelector('.ez-search-results').dataset.endResults === 'true';
  }

  get searchTerm() {
    return document.querySelector('form.ez-search-form.ez-input').dataset.searchTerm;
  }
}
